import { useCallback, useEffect, useState } from 'react';
import { getOrganizationPaymentGatewayPublicKey } from '~/services/modules/organizations';

export interface UsePaymentGatewayPublicKeyResult {
  publicKey: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

function isValidOrganizationId(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Busca a public_key do payment_gateway da organização (para token de cartão Pagar.me).
 * Dispara o fetch sempre que organizationId for uma string não vazia.
 */
export function usePaymentGatewayPublicKey(
  organizationId: string | null | undefined
): UsePaymentGatewayPublicKeyResult {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPublicKey = useCallback(async () => {
    if (!isValidOrganizationId(organizationId)) {
      setPublicKey(null);
      setError(null);
      return;
    }
    const id = organizationId.trim();
    setLoading(true);
    setError(null);
    try {
      const key = await getOrganizationPaymentGatewayPublicKey(id);
      setPublicKey(key);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setPublicKey(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!isValidOrganizationId(organizationId)) {
      setPublicKey(null);
      setError(null);
      return;
    }
    void fetchPublicKey();
  }, [organizationId, fetchPublicKey]);

  return { publicKey, loading, error, refetch: fetchPublicKey };
}
